/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 */

import React, { useEffect, useState, useCallback } from 'react';
import adminApi from '../../../services/adminApi';
import EntityTypeBadge from './EntityTypeBadge';
import LifecycleStateBadge from './LifecycleStateBadge';
import SealRiskBadge from './SealRiskBadge';

const TABS = [
  { key: 'overview',       label: 'Overview',       endpoint: (id) => `/api/admin/persona-agents/${id}` },
  { key: 'lifecycle',      label: 'Lifecycle',      endpoint: (id) => `/api/admin/persona-agents/${id}/lifecycle` },
  { key: 'configurations', label: 'Configurations', endpoint: (id) => `/api/admin/persona-agents/${id}/configurations` },
  { key: 'sessions',       label: 'Sessions',       endpoint: () => null },
  { key: 'audit',          label: 'Audit Log',      endpoint: () => '/api/admin/persona-agents/audit-log' },
];

/**
 * Classify an axios error into a normalized status the UI can branch on.
 * Same pattern used by the parent page — keeps 501 handling uniform.
 */
function classifyError(err) {
  const status = err?.response?.status;
  const body = err?.response?.data;
  if (status === 501 && body?.phase === 'scaffold') {
    return { kind: 'coming_soon', v2Endpoint: body.v2Endpoint || null };
  }
  if (status === 403) return { kind: 'permission', message: body?.error || 'Permission denied' };
  if (status === 404) return { kind: 'not_found', message: body?.error || 'Not found' };
  if (status === 401) return { kind: 'auth', message: 'Session expired' };
  return { kind: 'server', message: body?.error || err?.message || 'Failed to load' };
}

function ComingSoonPanel({ v2Endpoint }) {
  return (
    <div style={{
      padding: '24px',
      textAlign: 'center',
      color: '#64748b',
    }}>
      <div style={{
        display: 'inline-block',
        fontSize: '11px',
        fontWeight: 600,
        padding: '4px 12px',
        borderRadius: '12px',
        background: '#fef3c7',
        color: '#92400e',
        marginBottom: '12px',
      }}>
        Coming soon
      </div>
      <p style={{ margin: 0, fontSize: '14px' }}>
        This tab will populate when the upstream endpoint is live.
      </p>
      {v2Endpoint && (
        <p style={{
          margin: '8px 0 0',
          fontSize: '12px',
          fontFamily: 'monospace',
          color: '#94a3b8',
        }}>
          Pending: {v2Endpoint}
        </p>
      )}
    </div>
  );
}

function TabContent({ tabKey, data, error }) {
  if (error?.kind === 'coming_soon') {
    return <ComingSoonPanel v2Endpoint={error.v2Endpoint} />;
  }
  if (error?.kind === 'permission') {
    return (
      <div style={{ padding: '24px', color: '#991b1b', fontSize: '14px' }}>
        🔒 Missing permission to view this tab.
      </div>
    );
  }
  if (error?.kind === 'not_found') {
    return (
      <div style={{ padding: '24px', color: '#64748b', fontSize: '14px' }}>
        Not found.
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: '24px', color: '#991b1b', fontSize: '14px' }}>
        ⚠️ {error.message}
      </div>
    );
  }
  if (!data) {
    return (
      <div style={{ padding: '24px', color: '#64748b', fontSize: '14px' }}>
        Loading…
      </div>
    );
  }

  if (tabKey === 'sessions') {
    return <ComingSoonPanel v2Endpoint="GET /entities/:id/sessions (not yet defined)" />;
  }

  // Default rendering for tabs that DO have data: dump as readable JSON.
  // Replaced by feature-specific UI in Phase 2 once V2 contract is fixed.
  return (
    <pre style={{
      margin: 0,
      padding: '16px',
      background: '#0f172a',
      color: '#e2e8f0',
      fontSize: '12px',
      borderRadius: '8px',
      overflow: 'auto',
      maxHeight: '60vh',
    }}>
      {JSON.stringify(data, null, 2)}
    </pre>
  );
}

const DISABLED_ACTIONS = [
  { key: 'transition',  label: '🔁 Transition Lifecycle' },
  { key: 'pause',       label: '⏸️ Force Pause' },
  { key: 'cooling',     label: '❄️ Cooling Override' },
  { key: 'override',    label: '⚙️ Override Config' },
];

export default function PersonaAgentDetailDrawer({ entityId, entityHint, isOpen, onClose }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [tabData, setTabData] = useState({});      // { [tabKey]: data }
  const [tabError, setTabError] = useState({});    // { [tabKey]: classifiedError }
  const [tabLoading, setTabLoading] = useState({});

  const fetchTab = useCallback(async (tabKey) => {
    if (!entityId) return;
    const tab = TABS.find((t) => t.key === tabKey);
    if (!tab) return;
    const endpoint = tab.endpoint(entityId);
    if (!endpoint) {
      // No endpoint = always coming soon (e.g. sessions tab).
      setTabError((prev) => ({ ...prev, [tabKey]: { kind: 'coming_soon', v2Endpoint: null } }));
      return;
    }

    setTabLoading((prev) => ({ ...prev, [tabKey]: true }));
    setTabError((prev) => { const n = { ...prev }; delete n[tabKey]; return n; });

    try {
      const res = await adminApi.get(endpoint);
      const body = res?.data ?? res;
      setTabData((prev) => ({ ...prev, [tabKey]: body?.data ?? body }));
    } catch (e) {
      setTabError((prev) => ({ ...prev, [tabKey]: classifyError(e) }));
    } finally {
      setTabLoading((prev) => { const n = { ...prev }; delete n[tabKey]; return n; });
    }
  }, [entityId]);

  // Reset state and fetch overview every time the drawer opens for a new entity.
  useEffect(() => {
    if (!isOpen || !entityId) return;
    setActiveTab('overview');
    setTabData({});
    setTabError({});
    setTabLoading({});
    fetchTab('overview');
  }, [isOpen, entityId, fetchTab]);

  // Lazy-fetch when tab changes.
  useEffect(() => {
    if (!isOpen || !entityId) return;
    if (tabData[activeTab] === undefined && !tabError[activeTab] && !tabLoading[activeTab]) {
      fetchTab(activeTab);
    }
  }, [isOpen, entityId, activeTab, tabData, tabError, tabLoading, fetchTab]);

  if (!isOpen) return null;

  const header = tabData.overview || entityHint || {};
  // V2 P4 schema: `display_name` is canonical. Fall back through legacy
  // shapes only as drift insurance.
  const headerName = header?.display_name
    || header?.displayName
    || header?.name
    || header?.full_name
    || entityHint?.display_name
    || entityHint?.name
    || entityId
    || '—';
  const headerType = header?.entity_type || header?.type || entityHint?.entity_type;
  const headerState = header?.lifecycle_state || header?.state || entityHint?.lifecycle_state;
  const headerRisk = header?.seal_risk_score ?? header?.risk_score ?? entityHint?.seal_risk_score;

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15, 23, 42, 0.4)',
          zIndex: 999,
        }}
      />
      {/* Drawer */}
      <aside
        className="persona-agent-drawer"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100vh',
          width: 'min(560px, 100vw)',
          background: 'white',
          boxShadow: '-12px 0 32px rgba(15, 23, 42, 0.12)',
          zIndex: 1000,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header */}
        <div style={{ padding: '20px 24px', borderBottom: '1px solid #e2e8f0' }}>
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: '12px',
            marginBottom: '12px',
          }}>
            <h2 style={{
              margin: 0,
              fontSize: '18px',
              fontWeight: 700,
              color: '#1e293b',
              wordBreak: 'break-word',
            }}>
              {headerName}
            </h2>
            <button
              onClick={onClose}
              aria-label="Close drawer"
              style={{
                background: 'none',
                border: 'none',
                fontSize: '22px',
                color: '#64748b',
                cursor: 'pointer',
                lineHeight: 1,
                padding: '4px 8px',
              }}
            >
              ×
            </button>
          </div>
          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {headerType && <EntityTypeBadge type={headerType} />}
            {headerState && <LifecycleStateBadge state={headerState} />}
            <SealRiskBadge riskScore={headerRisk} />
          </div>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          borderBottom: '1px solid #e2e8f0',
          overflowX: 'auto',
        }}>
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              style={{
                padding: '12px 16px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: activeTab === t.key ? 600 : 400,
                color: activeTab === t.key ? '#7c3aed' : '#64748b',
                borderBottom: activeTab === t.key
                  ? '2px solid #7c3aed'
                  : '2px solid transparent',
                whiteSpace: 'nowrap',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <TabContent
            tabKey={activeTab}
            data={tabData[activeTab]}
            error={tabError[activeTab]}
          />
        </div>

        {/* Action footer — all disabled in Phase 1 */}
        <div style={{
          padding: '12px 16px',
          borderTop: '1px solid #e2e8f0',
          background: '#f8fafc',
          display: 'flex',
          gap: '8px',
          flexWrap: 'wrap',
        }}>
          {DISABLED_ACTIONS.map((a) => (
            <button
              key={a.key}
              disabled
              title="Coming soon — V2 endpoint not yet live"
              style={{
                padding: '6px 12px',
                background: 'white',
                color: '#94a3b8',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                fontSize: '12px',
                fontWeight: 500,
                cursor: 'not-allowed',
              }}
            >
              {a.label}
            </button>
          ))}
        </div>
      </aside>
    </>
  );
}
